import React from 'react';
import { connect } from 'react-redux';
import * as T from '../../../constants/types';
import { detectCityScale } from '../../../utils/cityScaleConfig';
import { getMinRadiusByZoom, calculateRadiusByCount } from '../../../utils/startsByHourRadiusCalc';
import { HourSlider } from '../HourSlider';
import { DateSelector } from './DateSelector';
import { withDisplayedLayersCheck } from '../../../HOC/withDisplayedLayersCheck';
import { t } from '@lingui/macro';
import { LegendRenderer } from '../../LegendRenderer/LegendRenderer';
import { STARTS_BY_HOUR_SIZE_LEGEND, STARTS_BY_HOUR_COLORS_LEGEND } from '../../../constants/legends';

const StartsByHourFilterComponent = ({ selectedHour, setSelectedHour, selectedDate, setSelectedDate }) => {
    // Обновление радиуса кругов при изменении selectedHour с использованием feature-state для анимации
    React.useEffect(() => {
        // eslint-disable-next-line no-underscore-dangle
        const mapInstance = window.__map;
        if (!mapInstance) return;

        try {
            const hourKey = `cnt_${selectedHour}`;
            const sourceId = 'martin.geo_starts_mvt';
            const layerId = 'Старты по часам';
            const layer = mapInstance.getLayer(layerId);
            if (!layer) {
                return;
            }

            // Получаем источник с правильным ID
            const source = mapInstance.getSource(sourceId);
            if (!source) {
                return;
            }

            // Устанавливаем фильтр: точки из текущего часа ИЛИ из предыдущего (для плавного исчезновения)
            // Это позволяет фичам плавно исчезать в feature-state пока они ещё видны на слое
            const prevHour = selectedHour === 0 ? 23 : selectedHour - 1;
            const prevHourKey = `cnt_${prevHour}`;
            const newFilter = [
                'any',
                [ '>', [ 'get', hourKey ], 0 ],
                [ '>', [ 'get', prevHourKey ], 0 ],
            ];
            mapInstance.setFilter(layerId, newFilter);

            // Получаем фичи с текущего часа (видимые в слое)
            const currentHourFeatures = mapInstance.querySourceFeatures(sourceId, {
                sourceLayer: 'martin.geo_starts_mvt',
                filter: [
                    '>', [ 'get', hourKey ], 0,
                ],
            });
            // Получаем фичи с предыдущего часа (для плавного исчезновения)
            const prevHourFeatures = mapInstance.querySourceFeatures(sourceId, {
                sourceLayer: 'martin.geo_starts_mvt',
                filter: [
                    '>', [ 'get', prevHourKey ], 0,
                ],
            });
            // Объединяем оба набора, убирая дубликаты
            const featureMap = new Map();
            currentHourFeatures.forEach(f => featureMap.set(f.id, f));
            prevHourFeatures.forEach(f => {
                if (!featureMap.has(f.id)) {
                    featureMap.set(f.id, f);
                }
            });
            const visibleFeatures = Array.from(featureMap.values());

            if (!visibleFeatures || visibleFeatures.length === 0) {
                return;
            }

            // Инициализируем feature-state для видимых фичей которых еще нет
            visibleFeatures.forEach(feature => {
                const featureState = mapInstance.getFeatureState({
                    source: sourceId,
                    sourceLayer: 'martin.geo_starts_mvt',
                    id: feature.id,
                });
                // Если feature-state нет (первый раз), инициализируем с 0 для плавного появления
                if (!featureState || !featureState.animatedRadius) {
                    mapInstance.setFeatureState(
                        { source: sourceId, sourceLayer: 'martin.geo_starts_mvt', id: feature.id },
                        { animatedRadius: 0 }
                    );
                }
            });

            // Параметры анимации
            const animationDuration = 300; // 500ms
            const startTime = Date.now();
            let animationFrame = null;
            // Кешируем стартовый радиус для каждой фичи ДО начала анимации
            const startRadiusCache = new Map();
            const currentZoomForStart = mapInstance.getZoom();
            const minRadiusForStart = getMinRadiusByZoom(currentZoomForStart);
            visibleFeatures.forEach(feature => {
                const featureState = mapInstance.getFeatureState({
                    source: sourceId,
                    sourceLayer: 'martin.geo_starts_mvt',
                    id: feature.id,
                });
                // Используем текущий animatedRadius или вычисляем из предыдущего часа
                if (featureState && 'animatedRadius' in featureState) {
                    startRadiusCache.set(feature.id, featureState.animatedRadius);
                } else {
                    // Вычисляем из предыдущего часа с учётом текущего zoom
                    const prevCountValue = parseFloat(feature.properties[prevHourKey]) || 0;
                    const prevRadius = prevCountValue <= 0
                        ? 0
                        : prevCountValue <= 1
                            ? minRadiusForStart
                            : prevCountValue >= 100
                                ? 100
                                : minRadiusForStart + ((prevCountValue - 1) / (100 - 1)) * (100 - minRadiusForStart);
                    startRadiusCache.set(feature.id, prevRadius);
                }
            });
            // Кешируем текущий город один раз (не пересчитываем на каждом frame анимации)
            const mapCenter = mapInstance.getCenter();
            const cityScale = detectCityScale(mapCenter);
            const isMajorCity = cityScale === 'major';
            const getTargetRadius = feature => {
                const countValue = parseFloat(feature.properties[hourKey]) || 0;
                // Пересчитываем zoom на каждый frame (может меняться во время анимации)
                const currentZoom = mapInstance.getZoom();
                const minRadius = getMinRadiusByZoom(currentZoom);
                // Используем утилиту для расчёта радиуса
                return calculateRadiusByCount(countValue, isMajorCity, minRadius);
            };
            const animate = () => {
                const elapsed = Date.now() - startTime;
                const progress = Math.min(elapsed / animationDuration, 1);
                // Анимируем видимые фичи
                visibleFeatures.forEach(feature => {
                    const startRadius = startRadiusCache.get(feature.id);
                    const targetRadius = getTargetRadius(feature);
                    // Линейная интерполяция
                    const currentRadius = startRadius + (targetRadius - startRadius) * progress;
                    mapInstance.setFeatureState(
                        { source: sourceId, sourceLayer: 'martin.geo_starts_mvt', id: feature.id },
                        { animatedRadius: currentRadius }
                    );
                });
                if (progress < 1) {
                    animationFrame = requestAnimationFrame(animate);
                }
            };

            // Начинаем анимацию
            animate();

            // Очистка при unmount или при новом вызове
            return () => {
                if (animationFrame) {
                    cancelAnimationFrame(animationFrame);
                }
            };
        } catch (err) {
            // Ошибка при обновлении слоя, игнорируем
        }
    }, [ selectedHour ]);

    // Обновляем размеры при изменении zoom без анимации
    React.useEffect(() => {
        // eslint-disable-next-line no-underscore-dangle
        const mapInstance = window.__map;
        if (!mapInstance) return;

        const hourKey = `cnt_${selectedHour}`;
        const sourceId = 'martin.geo_starts_mvt';
        const layerId = 'Старты по часам';
        const layer = mapInstance.getLayer(layerId);
        if (!layer) {
            return;
        }
        // Функция обновления размеров при zoom
        let zoomUpdateTimeout = null;
        const MIN_ZOOM_FOR_LAYER = 9; // Слой "Старты по часам" отображается только с этого zoom
        const handleZoomChange = () => {
            const currentZoom = mapInstance.getZoom();
            // Не обновляем если zoom меньше минимального - слой не виден
            if (currentZoom < MIN_ZOOM_FOR_LAYER) {
                return;
            }
            // Debounce: ждём 150ms после последнего zoom-события перед обновлением
            if (zoomUpdateTimeout) {
                clearTimeout(zoomUpdateTimeout);
            }
            zoomUpdateTimeout = setTimeout(() => {
                const minRadius = getMinRadiusByZoom(currentZoom);
                const mapCenter = mapInstance.getCenter();
                const cityScale = detectCityScale(mapCenter);
                const isMajorCity = cityScale === 'major';
                // Получаем все видимые фичи
                const visibleFeatures = mapInstance.querySourceFeatures(sourceId, {
                    sourceLayer: 'martin.geo_starts_mvt',
                });

                // Обновляем размер для каждой фичи без анимации
                visibleFeatures.forEach(feature => {
                    const countValue = parseFloat(feature.properties[hourKey]) || 0;
                    if (countValue <= 0) {
                        return;
                    }

                    const newRadius = calculateRadiusByCount(countValue, isMajorCity, minRadius);

                    mapInstance.setFeatureState(
                        { source: sourceId, sourceLayer: 'martin.geo_starts_mvt', id: feature.id },
                        { animatedRadius: newRadius }
                    );
                });
            }, 150);
        };

        // Добавляем слушатель события zoom
        mapInstance.on('zoom', handleZoomChange);

        // Очистка при unmount
        return () => {
            mapInstance.off('zoom', handleZoomChange);
            if (zoomUpdateTimeout) {
                clearTimeout(zoomUpdateTimeout);
            }
        };
    }, [ selectedHour ]);

    // Обновляем слой при изменении selectedDate
    // Обновляем фильтр слоя когда меняется выбранный час
    React.useEffect(() => {
        // eslint-disable-next-line no-underscore-dangle
        const mapInstance = window.__map;
        if (!mapInstance) return;

        try {
            const hourKey = `cnt_${selectedHour}`;
            const layerId = 'Старты по часам';
            const layer = mapInstance.getLayer(layerId);
            if (!layer) {
                return;
            }

            // Переустанавливаем фильтр - это заставляет слой перерисоваться
            const newFilter = [
                '>', [ 'get', hourKey ], 0,
            ];
            mapInstance.setFilter(layerId, newFilter);
        } catch (err) {
            console.error('[Starts By Hour] Error updating filter:', err);
        }
    }, [ selectedHour ]);
    // DA-14953: контролы выбора даты и часа вынесены в оверлей-таймлайн внизу карты
    // (StartsByHourTimeline). Компонент остаётся смонтированным ради эффектов выше
    // (setFilter + анимация feature-state) — они питают и таймлайн.
    // LEGACY: контролы сайдбара ниже скрыты, но пока не удалены. Удалить после
    // полной обкатки таймлайна.
    const SHOW_LEGACY_SIDEBAR_CONTROLS = false;
    if (!SHOW_LEGACY_SIDEBAR_CONTROLS) return null;
    return (
        <div>
            <DateSelector
                value={selectedDate}
                onChange={setSelectedDate}
            />
            <HourSlider
                value={selectedHour}
                onChange={hour => setSelectedHour(hour)}
            />
        </div>
    );
};
const mapStateToProps = ({ map: { selectedHour, selectedDate } }) => ({ selectedHour, selectedDate });
const mapDispatchToProps = dispatch => ({
    setSelectedHour: hour => dispatch({
        type: T.SET_SELECTED_HOUR,
        payload: { selectedHour: hour },
    }),
    setSelectedDate: date => dispatch({
        type: T.SET_SELECTED_DATE,
        payload: { selectedDate: date },
    }),
});
const ConnectedComponent = connect(mapStateToProps, mapDispatchToProps)(StartsByHourFilterComponent);

export const StartsByHourFilter = withDisplayedLayersCheck(
    ConnectedComponent,
    t`Старты по часам`
);

// Legend component for Starts by Hour layer
const StartsByHourLegendComponent = () => {
    return (
        <div style={{ padding: '0 10px' }}>
            {/* Size legend - using new SizeGradientLegend */}
            <LegendRenderer config={STARTS_BY_HOUR_SIZE_LEGEND} />

            <div style={{ marginBottom: '16px' }} />

            {/* Color legend - using new ColoredItemsLegend */}
            <LegendRenderer config={STARTS_BY_HOUR_COLORS_LEGEND} />

            <div style={{ marginBottom: 32 }} />
        </div>
    );
};

export const StartsByHourLegend = withDisplayedLayersCheck(
    StartsByHourLegendComponent,
    t`Старты по часам`
);
